/**
 * CompsSummary Component
 * Displays summary statistics and quick insights for film comps
 */

import React from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Grid, 
  IconButton, 
  Tooltip,
  LinearProgress
} from '@mui/material';
import { 
  CheckCircle, 
  AllInclusive,
  Refresh,
  Info
} from '@mui/icons-material';

const CompsSummary = ({ 
  totalCount = 0, 
  greenlightCount = 0,
  onRefresh,
  loading = false
}) => {
  const summaryCards = [
    {
      title: 'Total Comps',
      value: totalCount,
      icon: <AllInclusive />,
      color: '#6B7280',
      description: 'All comparisons found'
    },
    {
      title: 'Greenlight Comps',
      value: greenlightCount,
      icon: <CheckCircle />,
      color: '#10B981',
      description: 'Expert-curated comparisons'
    }
  ];

  return (
    <Paper 
      sx={{ 
        p: 3, 
        mb: 3,
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        border: '1px solid #e2e8f0',
        borderRadius: 2
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Summary
        </Typography>
        <Tooltip title="Refresh data">
          <IconButton 
            onClick={onRefresh} 
            disabled={loading}
            size="small"
            sx={{ 
              color: 'text.secondary',
              '&:hover': { color: 'primary.main' }
            }}
          >
            <Refresh sx={{ 
              fontSize: '1.2rem',
              animation: loading ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={2}>
        {summaryCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Box
              sx={{
                p: 2,
                backgroundColor: 'white',
                borderRadius: 2,
                border: '1px solid #e2e8f0',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  borderColor: card.color + '40'
                }
              }}
            >
              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      p: 0.5,
                      borderRadius: 1,
                      backgroundColor: card.color + '15',
                      color: card.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {card.icon}
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'text.secondary',
                      fontSize: '0.75rem'
                    }}
                  >
                    {card.title}
                  </Typography>
                </Box>
                <Tooltip title={card.description}>
                  <Info sx={{ fontSize: '0.9rem', color: 'text.disabled' }} />
                </Tooltip>
              </Box>

              {/* Value */}
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700, 
                  color: card.color,
                  mb: 1,
                  lineHeight: 1
                }}
              >
                {card.value}
              </Typography>

              {/* Progress bar for percentages */}
              {card.percentage !== undefined && (
                <Box sx={{ mt: 'auto' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {card.percentage.toFixed(1)}% of total
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={card.percentage}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: card.color + '20',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: card.color,
                        borderRadius: 2
                      }
                    }}
                  />
                </Box>
              )}

              {/* Description for non-percentage cards */}
              {card.percentage === undefined && (
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ mt: 'auto', fontSize: '0.7rem' }}
                >
                  {card.description}
                </Typography>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Quick Insights */}
      {totalCount > 0 && (
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e2e8f0' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Quick Insights:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {greenlightCount > 0 && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                fontSize: '0.75rem',
                color: 'text.secondary'
              }}>
                <CheckCircle sx={{ fontSize: '0.9rem', color: '#10B981' }} />
                <span>{greenlightCount} expert-curated comp{greenlightCount !== 1 ? 's' : ''}</span>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default CompsSummary;
